import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import { EnumTipoPeaje } from '../../api'
import { PriceCoefficientTable } from '../../api/Argentia/endpoints/priceCoefficients/model/PriceCoefficient'
import {
  ArgentiaPricingTableModel,
  getPriceComponentModelV2RelatedEntityPropertyPath,
  PriceComponentInstancesDataTableModel,
  PriceComponentModelV2,
  PriceComponentTypeModel,
  PriceComponentV2RequestModel
} from '../../api/Argentia/endpoints/priceComponents'
import { PricePeriodTableModel } from '../../api/Argentia/endpoints/pricePeriods/model/pricePeriods'
import { PriceModelAPI } from '../../api/Argentia/endpoints/pricing/model/prices'
import {
  PricePeriodValueGroupedModel,
  PricePeriodValueRequestModel,
  PriceValuesModel
} from '../../api/Argentia/endpoints/pricing/model/values'
import { BatchVersionSingleRequest } from '../../api/Argentia/endpoints/versions/model/batchVersionSingleRequest.model'
import {
  EnumMarketType,
  EnumPriceCalculationType,
  EnumPriceComponentReference,
  EnumRedondeo
} from '../../api/Argentia/enums'
import { EnumCommodity } from '../../api/Argentia/enums/commodity'
import { EnumHora } from '../../api/Argentia/enums/hora'
import { EditablePriceModel } from '../../api/Argentia/model/editablePriceModel'
import { NewPriceResponseModel } from '../../api/Argentia/model/newPriceResponseModel'
import { NewPriceRequest } from '../../api/Argentia/requests/newPrice.request'
import { EnumPrecioUnitario } from '../../api/oData/enums/enumPrecioUnitario'
import { EnumPresionTension } from '../../api/oData/enums/enumPresionTension'
import { EnumPriceType } from '../../api/oData/enums/enumPriceType'
import {
  enumEnumToValueNumber,
  enumEnumToValueString,
  enumKeyToValue,
  enumValueToKey
} from '../../helpers'
import { formatDateOnlyDate, formatDateOnlyString } from '../../helpers/formatDateOnly'
import {
  mapValuesFromFormData,
  newPriceCoefficientsRequest
} from '../priceComponents/priceComponents.mapper'
import {
  PriceComponentFormData,
  PriceComponentTypeFormData
} from './priceComponentType/components/priceComponentTypeForm'
import {
  NewPriceModel,
  NewPriceVersion,
  PriceDetails,
  PriceValues,
  PriceVersion,
  PricingTableModel
} from './pricing.Model'

export const mapPriceDetails = (price): PriceDetails => {
  return (
    price && {
      ...price,
      marketType: enumValueToKey(EnumMarketType, price.marketType),
      precioUnitario: enumValueToKey(EnumPrecioUnitario, price.precioUnitario),
      redondeo: enumValueToKey(EnumRedondeo, price.redondeo),
      priceExpression: price.expression
    }
  )
}

export const mapPriceModelAPIToPriceDetails = (price: PriceModelAPI): PriceDetails | null => {
  return price
    ? {
        ...price,
        commodity: price.commodity ? enumEnumToValueNumber(EnumCommodity, price.commodity)! : null,
        priceType: enumEnumToValueNumber(EnumPriceType, price.priceType)!,
        description: price.description ?? '',
        empresa: +price.idEmpresa,
        precioUnitario: enumEnumToValueNumber(EnumPrecioUnitario, price.precioUnitario)!,
        redondeo: enumEnumToValueNumber(EnumRedondeo, price.redondeo)!,
        priceCalculationType: enumEnumToValueNumber(
          EnumPriceCalculationType,
          price.priceCalculationType
        )!,
        relatedFuturePrice: null,
        priceExpression: price.expression,
        priceConceptId: 0
      }
    : null
}

export const mapNewPricePOST = (
  item: NewPriceModel,
  companyId: number | undefined,
  expression?: string,
  priceUnit?: EnumPrecioUnitario,
  priceCalculationType?: EnumPriceCalculationType
): NewPriceRequest => {
  return (
    item && {
      code: item.code,
      priceType: item.priceType,
      commodity: item.commodity,
      empresa: companyId,
      precioUnitario: item.precioUnitario || priceUnit,
      redondeo: item.redondeo,
      description: item.description,
      marketType: item.marketType,
      expression: expression || null,
      priceCalculationType: priceCalculationType,
      values: []
    }
  )
}

export const mapPriceFromAPI = (
  price: NewPriceResponseModel,
  commodity: EnumCommodity,
  priceType: EnumPriceType
): NewPriceModel => {
  return (
    price && {
      id: price.id,
      code: price.code,
      description: price.description,
      precioUnitario: price.precioUnitario,
      marketType: price.marketType,
      commodity: commodity,
      priceType: priceType,
      redondeo: price.redondeo,
      relatedFuturePrice: null,
      priceExpression: price.priceExpression,
      priceCalculationType: price.priceCalculationType
    }
  )
}

export const mapValuesFromAPI = (
  priceValues: PriceValuesModel[],
  priceType: EnumPriceType
): PriceValues[] => {
  return priceValues && priceValues.map(value => mapValueFromAPI(value, priceType))
}

const mapValueFromAPI = (priceValue: PriceValuesModel, priceType: EnumPriceType): PriceValues => {
  return (
    priceValue && {
      ...priceValue,
      hora: enumValueToKey(EnumHora, priceValue.hora),
      canEdit:
        priceType === EnumPriceType.Formula
          ? false
          : priceType === EnumPriceType.Futuro
            ? moment(priceValue.endDate).isBefore(moment(), 'days')
              ? false
              : true
            : true
    }
  )
}

export const mapFieldsToNewValuePost = (priceType, marketType, item: PriceValues): PriceValues => {
  const { canEdit, ...value } = item
  if (
    priceType === EnumPriceType.Cotizacion &&
    (marketType === EnumMarketType.Horario || marketType === EnumMarketType.Diario)
  ) {
    return (
      value && {
        ...value,
        endDate: value.startDate,
        manual: true
      }
    )
  }
  return value
}

export const mapNewValuePOST = (item: PriceValues): any => {
  const { canEdit, ...value } = item
  return (
    value && [
      {
        id: 0,
        value: value.value,
        startDate: value.startDate,
        endDate: value.endDate || null,
        manual: true,
        future: false,
        hora: value.hora,
        qHora: value.qHora
      }
    ]
  )
}

export const mapUpdateValuePOST = (item: PriceValues): any => {
  return (
    item && [
      {
        value: item.value,
        startDate: item.startDate,
        endDate: item.endDate || null,
        manual: true,
        future: item.future,
        hora: item.hora,
        qHora: item.qHora
      }
    ]
  )
}

export const timeFramesElementsMapperToDropdown = (items): SelectOption[] => {
  return (
    items &&
    items.map(item => {
      const time = new Date()
      time.setHours(item.hora)
      if (item.veranoInvierno) {
        const season = item.veranoInvierno === 1 ? 'Invierno' : 'Verano'
        return {
          value: item.hora,
          label: `${moment(item.hora, 'HH').format('HH:mm')} (${season})`
        }
      }
      return {
        value: item.hora,
        label: moment(item.hora, 'HH').format('HH:mm')
      }
    })
  )
}

export const futurePricesMapperToDropdown = (items): SelectOption[] => {
  return (
    items &&
    items.map(item => {
      return { value: item.id, label: item.code }
    })
  )
}

export const mapSingleRequestToBatch = (versions, priceId: number): BatchVersionSingleRequest[] => {
  return versions.map((version, idx) => {
    if (version.method === 'PATCH') {
      delete version.method
      return {
        id: String(idx + 1),
        method: 'PATCH',
        atomicityGroup: 'g1',
        body: version.version
      }
    } else if (version.method === 'POST') {
      delete version.method
      return {
        id: String(idx + 1),
        method: 'POST',
        atomicityGroup: 'g1',
        body: {
          ...version.version,
          price: {
            '@odata.id': `prices(${priceId})`
          }
        }
      }
    } else if (version.method === 'DELETE') {
      return {
        id: String(idx + 1),
        method: version.method,
        atomicityGroup: 'g1',
        body: { id: version.version.id }
      }
    }
  })
}

export const sortVersionsByStartDate = (versionsArray: any[]): NewPriceVersion[] => {
  const sortedArray = versionsArray.sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  )

  return sortedArray
}

export const sortPriceVersionsByStartDate = (versionsArray: any[]): PriceVersion[] => {
  const sortedArray = versionsArray.sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  )

  return sortedArray
}

export const mapArgentiaPricingTableModelToPricingTableModel = (
  apiResponse: ArgentiaPricingTableModel[]
): PricingTableModel[] => {
  return apiResponse.map(element => mapArgentiaPricingTableModelElementToPricingTableModel(element))
}

const mapArgentiaPricingTableModelElementToPricingTableModel = (
  apiResponseElement: ArgentiaPricingTableModel
): PricingTableModel => {
  return {
    id: apiResponseElement.idPrice!,
    code: apiResponseElement.priceCode!,
    description: apiResponseElement.priceDescription!,
    precioUnitario: apiResponseElement.version?.unitPrice!,
    startDate: apiResponseElement.version?.startDate!,
    endDate: apiResponseElement.version?.endDate!,
    idPriceComponent: apiResponseElement.idPriceComponent,
    idPriceComponentType: apiResponseElement.idPriceComponentType,
    idDummyPrice: apiResponseElement.idDummyPrice,
    idVersion: apiResponseElement.version?.idVersion,
    priceComponentTypeCode: apiResponseElement.priceComponentTypeCode,
    priceComponentTypeDescription: apiResponseElement.priceComponentTypeDescription,
    priceType: apiResponseElement.priceType,
    priceComponentTypeReference: apiResponseElement.priceComponentTypeReference,
    rounding: apiResponseElement.version?.rounding,
    expression: apiResponseElement.version?.expression,
    canEditPriceComponentType:
      apiResponseElement.priceType === EnumPriceType.Parametro ||
      apiResponseElement.priceType === EnumPriceType.ParametroPeriodo ||
      apiResponseElement.priceType === EnumPriceType.Apuntamiento,
    canViewPriceComponentTypeInstances:
      apiResponseElement.priceType === EnumPriceType.Parametro ||
      apiResponseElement.priceType === EnumPriceType.ParametroPeriodo ||
      apiResponseElement.priceType === EnumPriceType.Apuntamiento,
    relatedPriceComponentCount: apiResponseElement.relatedPriceComponentCount,
    marketType: apiResponseElement.marketType,
    priceCalculationType: apiResponseElement.version?.priceCalculationType
  }
}

export const mapPricingTableModelToEditablePriceModel = (
  rowData: PricingTableModel
): EditablePriceModel => {
  return {
    id: rowData.idDummyPrice,
    expression: rowData.expression,
    priceCalculationType: rowData.priceCalculationType,
    precioUnitario: enumEnumToValueNumber(EnumPrecioUnitario, rowData.precioUnitario),
    redondeo: rowData.rounding,
    marketType: rowData.marketType
  }
}

export const mapEditableModelPriceFields = (price: any): EditablePriceModel => {
  return {
    id: price.id,
    expression: price.expression,
    priceCalculationType: enumKeyToValue(EnumPriceCalculationType, price.priceCalculationType),
    precioUnitario: enumKeyToValue(EnumPrecioUnitario, price.precioUnitario),
    redondeo: enumKeyToValue(EnumRedondeo, price.redondeo),
    marketType: price.marketType
  }
}

export const mapFormDataToPriceComponentTypeRequest = (
  formData: PriceComponentTypeFormData
): PriceComponentTypeModel => {
  return {
    id: formData.id,
    code: formData.code!,
    description: formData.description,
    priceDummy: {
      id: formData.idDummyPrice,
      priceType: EnumPriceType.Dummy,
      unitPrice: formData.unitPrice!,
      rounding: formData.rounding,
      priceCalculationType: formData.priceCalculationType,
      expression: formData.expression!
    },
    referenceType: formData.referenceType!,
    priceType: formData.priceType,
    commodity: formData.commodity,
    company: formData.company!,
    country: formData.country!
  }
}

export const mapPricingTableModelToPriceComponentTypeForm = (
  tableData: PricingTableModel | undefined
): PriceComponentTypeFormData | undefined => {
  return (
    tableData && {
      id: tableData.idPriceComponentType,
      idDummyPrice: tableData.idDummyPrice,
      code: tableData.priceComponentTypeCode!,
      description: tableData.priceComponentTypeDescription,
      referenceType: tableData.priceComponentTypeReference!,
      commodity: tableData.commodity!,
      priceType: tableData.priceType!,
      unitPrice: enumEnumToValueNumber(EnumPrecioUnitario, tableData.precioUnitario)!,
      rounding: tableData.rounding!
    }
  )
}

export const mapDataToPriceComponentFormData = (
  priceComponentTypeId: number,
  formData: PriceComponentTypeFormData
): PriceComponentFormData => {
  return {
    priceComponentTypeId: priceComponentTypeId,
    priceComponentTypeCode: formData.code!,
    priceComponentTypeDesc: formData.description,
    priceType: formData.priceType,
    commodity: formData.commodity,
    code: `${formData.code}_base`,
    description: formData.description ? `${formData.description}_base` : 'base',
    unitPrice: formData.unitPrice,
    rounding: formData.rounding,
    startDate: moment().startOf('year').toDate()
  }
}

export const mapPriceComponentFormDataToRequest = (
  formData: PriceComponentFormData,
  priceCoefficientsData?: PriceCoefficientTable[]
): PriceComponentV2RequestModel => {
  return {
    price: {
      priceType: formData.priceType,
      unitPrice: formData.unitPrice!,
      rounding: formData.rounding,
      values: mapValuesFromFormData(formData),
      coefficients:
        priceCoefficientsData && priceCoefficientsData.length > 0
          ? newPriceCoefficientsRequest(priceCoefficientsData)
          : undefined,
      expression: ''
    },
    priceComponentType: {
      id: formData.priceComponentTypeId
    }
  }
}

export const mapPriceComponentModelV2ToPriceComponentInstanceDataTable = (
  response: PriceComponentModelV2[]
): PriceComponentInstancesDataTableModel[] => {
  return response.map(item => mapPriceComponentModelV2ToPriceComponentInstanceDataTableItem(item))
}

export const mapPriceComponentModelV2ToPriceComponentInstanceDataTableItem = (
  item: PriceComponentModelV2
): PriceComponentInstancesDataTableModel => {
  const referenceTypeNumber = enumEnumToValueNumber(
    EnumPriceComponentReference,
    item.priceComponentType.referenceType
  )

  const path = getPriceComponentModelV2RelatedEntityPropertyPath(referenceTypeNumber)

  const nullProperty = path === 'invalidEntity' || path === undefined

  let entityDescription: string | null = null
  if (!nullProperty) {
    switch (referenceTypeNumber) {
      case EnumPriceComponentReference.Peaje:
        entityDescription = enumEnumToValueString(EnumTipoPeaje, item[path]) ?? null
        break
      case EnumPriceComponentReference.PresionTension:
        entityDescription = enumEnumToValueString(EnumPresionTension, item[path]) ?? null
        break
      default:
        entityDescription = item[path]?.description ?? null
        break
    }
  }

  return {
    ...item,
    priceComponentType: {
      ...item.priceComponentType,
      referenceType: enumEnumToValueNumber(
        EnumPriceComponentReference,
        item.priceComponentType.referenceType
      ),
      commodity: enumEnumToValueNumber(EnumCommodity, item.priceComponentType.commodity)
    },
    referenceTypeEntityPath: !nullProperty ? path : null,
    referenceTypeEntityCode: !nullProperty ? item[path]?.code : null,
    referenceTypeEntityDescription: !nullProperty ? entityDescription : null,
    isDefaultValue: item.isDefaultValue ?? false
  } as PriceComponentInstancesDataTableModel
}

export const mapPricePeriodTable = (response: PricePeriodTableModel): PricePeriodTableModel => {
  return {
    ...response,
    groupedValues: response.groupedValues.map(item => mapPricePeriod(item))
  }
}

const mapPricePeriod = (item: PricePeriodValueGroupedModel): PricePeriodValueGroupedModel => {
  return {
    ...item,
    startDate: formatDateOnlyDate(item.startDate)!,
    endDate: formatDateOnlyDate(item.endDate)
  }
}
